import { useState, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Pencil,
  Check,
  X,
  AlertTriangle,
  FileText,
  Mic,
  MessageSquare,
  Sparkles,
  Brain,
  User,
} from 'lucide-react'
import type { ContextChunkSummary } from '@/types/contextEngine'

// ─── Source type display ────────────────────────────────────────────────────

const SOURCE_CONFIG: Record<
  ContextChunkSummary['sourceType'],
  { icon: typeof FileText; label: string }
> = {
  document: { icon: FileText, label: 'Document' },
  audio: { icon: Mic, label: 'Audio' },
  interview: { icon: MessageSquare, label: 'Interview' },
  artifact_feedback: { icon: Sparkles, label: 'Feedback' },
  manual_edit: { icon: User, label: 'Manual' },
  inference: { icon: Brain, label: 'Inferred' },
}

function confidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'bg-accent-success/15 text-accent-success'
  if (confidence >= 0.5) return 'bg-accent-amber/15 text-accent-amber'
  return 'bg-accent-error/15 text-accent-error'
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface ChunkCardProps {
  chunk: ContextChunkSummary
  onUpdate: (chunkId: string, updates: { text?: string }) => Promise<void>
  onArchive: (chunkId: string) => Promise<void>
}

export function ChunkCard({ chunk, onUpdate, onArchive }: ChunkCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(chunk.text ?? '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const source = SOURCE_CONFIG[chunk.sourceType]
  const SourceIcon = source.icon

  // Auto-focus and resize textarea when editing
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [isEditing])

  const handleEdit = useCallback(() => {
    setEditText(chunk.text ?? '')
    setSaveError(null)
    setIsEditing(true)
  }, [chunk.text])

  const handleCancel = useCallback(() => {
    setIsEditing(false)
    setEditText(chunk.text ?? '')
  }, [chunk.text])

  const handleSave = useCallback(async () => {
    if (editText.trim() === (chunk.text ?? '').trim()) {
      setIsEditing(false)
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      await onUpdate(chunk.chunkId, { text: editText.trim() })
      setIsEditing(false)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }, [editText, chunk.text, chunk.chunkId, onUpdate])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel()
      if (e.key === 'Enter' && e.metaKey) handleSave()
    },
    [handleCancel, handleSave],
  )

  const dateLabel = new Date(chunk.updatedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })

  return (
    <div className="group rounded-lg border border-border bg-bg-secondary p-4 transition-colors hover:border-border-hover">
      {/* Header row */}
      <div className="mb-2 flex items-center gap-2">
        {/* Source badge */}
        <span className="inline-flex items-center gap-1 rounded-sm bg-bg-tertiary px-2 py-0.5 text-xs text-text-tertiary">
          <SourceIcon size={12} />
          {source.label}
        </span>

        {/* Sub-domain */}
        {chunk.subDomain && (
          <span className="rounded-sm bg-bg-tertiary px-2 py-0.5 text-xs text-text-tertiary">
            {chunk.subDomain}
          </span>
        )}

        {/* Confidence badge */}
        <span
          className={`ml-auto rounded-sm px-2 py-0.5 font-mono text-xs ${confidenceColor(chunk.confidence)}`}
        >
          {Math.round(chunk.confidence * 100)}%
        </span>

        {/* Contradiction indicator */}
        {chunk.isContradicted && (
          <span
            className="inline-flex items-center gap-1 rounded-sm bg-accent-error/10 px-2 py-0.5 text-xs text-accent-error"
            title={`Contradicted by ${chunk.contradictedBy ?? 'another chunk'}`}
          >
            <AlertTriangle size={12} />
            Conflict
          </span>
        )}
      </div>

      {/* Content — view or edit */}
      <AnimatePresence mode="wait" initial={false}>
        {isEditing ? (
          <motion.div
            key="edit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <textarea
              ref={textareaRef}
              value={editText}
              onChange={(e) => {
                setEditText(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = `${e.target.scrollHeight}px`
              }}
              onKeyDown={handleKeyDown}
              className="w-full resize-none rounded-md border border-accent-amber/30 bg-bg-tertiary p-3 font-body text-sm text-text-primary outline-none focus:border-accent-amber"
              rows={3}
              disabled={saving}
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-accent-amber px-3 py-1.5 text-xs font-semibold text-bg-deepest transition-colors hover:bg-accent-amber-hover disabled:opacity-50"
              >
                <Check size={14} />
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-bg-tertiary px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-bg-tertiary/80"
              >
                <X size={14} />
                Cancel
              </button>
              <span className="ml-auto text-xs text-text-tertiary">
                ⌘Enter to save · Esc to cancel
              </span>
            </div>
            {saveError && (
              <p className="mt-1 text-xs text-accent-error">{saveError}</p>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <p className="mb-2 whitespace-pre-wrap font-body text-sm leading-relaxed text-text-primary line-clamp-4">
              {chunk.text ?? '(content not loaded)'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div className="mt-2 flex items-center gap-2 text-xs text-text-tertiary">
        <span className="font-mono">{dateLabel}</span>
        <span>·</span>
        <span>v{chunk.version}</span>

        {/* Actions — visible on hover */}
        <div className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {!isEditing && (
            <button
              onClick={handleEdit}
              className="rounded p-1 text-text-tertiary transition-colors hover:bg-bg-tertiary hover:text-accent-amber"
              title="Edit chunk"
            >
              <Pencil size={14} />
            </button>
          )}
          <button
            onClick={() => {
              if (window.confirm('Archive this chunk? It will be hidden from all views.')) {
                onArchive(chunk.chunkId)
              }
            }}
            className="rounded p-1 text-text-tertiary transition-colors hover:bg-accent-error/10 hover:text-accent-error"
            title="Archive chunk"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
